// aiChatClientV3.ts - Version 3 with direct server results

import { SpacesName } from "../stores/aiChatStore";
import { SearchResultSpaceInfo } from "./aiChatClient";

/**
 * Represents a single message in the chat history for V3.
 */
export interface ChatMessageV3 {
  uuid: string | undefined;
  role: "user" | "assistant" | "system";
  content: string;
  rawContent?: string;
  result?: SearchResultV3; // Direct search results from server
  metadata?: {
    searchMode: "global" | "current";
    selectedSpaces?: string[];
    planInfo?: {
      propertyName: string;
      planName: string;
    };
    timestamp?: string;
  };
}

/**
 * Structure of the request body sent to the /ai/v3/search-spaces endpoint.
 */
export interface ChatRequestBodyV3 {
  userPrompt: string;
  messageHistory?: ChatMessageV3[];
  spacesNames: SpacesName[];
}

/**
 * Search criteria item for V3
 */
export interface SearchCriteriaItemV3 {
  table: string;
  field: string;
  operator: string;
  value: any;
  confidence: number;
}

/**
 * Reference space criteria for V3
 */
export interface ReferenceSpaceCriteriaV3 {
  table: string;
  field: string;
  operator: string;
  value: string;
  confidence: number;
}

/**
 * Order by clause for V3
 */
export interface OrderByV3 {
  field: string;
  direction: "ASC" | "DESC";
}

/**
 * Search criteria structure for V3
 */
export interface SearchCriteriaV3 {
  success: boolean;
  searchCriteria: SearchCriteriaItemV3[];
  referenceSpaceCriteria: ReferenceSpaceCriteriaV3[];
  logic: string;
  joins: any[];
  orderBy: OrderByV3[];
  limit: number;
  confidence: number;
  limitations: string[];
  suggestions: string[];
}

/**
 * Reference space information
 */
export interface ReferenceSpaceV3 {
  space_number: string;
  space_header: string;
  gla: string;
  distance_m: number | null;
  lease_status: string | null;
  rent_per_m2: string;
  lease_start_date: string;
  lease_end_date: string;
}

/**
 * Space result from search
 */
export interface SpaceResultV3 {
  space_number: string;
  space_header: string;
  gla: string;
  distance_m: string;
  lease_status: string | null;
  rent_per_m2: string;
  lease_start_date: string;
  lease_end_date: string;
  total_rent: string;
}

/**
 * Complete search result structure for V3
 */
export interface SearchResultV3 {
  success: boolean;
  searchCriteria: SearchCriteriaV3;
  reference_space: ReferenceSpaceV3 | null;
  spaces: SpaceResultV3[];
  searchResults: SearchResultSpaceInfo[] | null;
  totalCount: number;
  confidence: number;
}

/**
 * Structure of a successful response from the /ai/v3/search-spaces endpoint.
 */
export interface ChatSuccessResponseV3 {
  success: boolean;
  searchCriteria: SearchCriteriaV3;
  reference_space: ReferenceSpaceV3 | null;
  spaces: SpaceResultV3[];
  searchResults: SearchResultSpaceInfo[] | null;
  totalCount: number;
  confidence: number;
}

/**
 * Structure for error responses from the backend API.
 */
export interface BackendErrorResponseV3 {
  error: string;
  details?: string;
}

/**
 * Calls the backend AI chat V3 API endpoint.
 *
 * @param apiUrl - The full URL of the chat endpoint (e.g., 'http://localhost:3000/ai/v3/search-spaces').
 * @param userPrompt - The user's current message.
 * @param messageHistory - An array of previous messages in the conversation.
 * @param spacesNames - Array of space names with their DBAs.
 * @returns A promise that resolves with the ChatSuccessResponseV3 object.
 * @throws An error if the API call fails or returns an error status.
 */
export async function callChatApiV3(
  apiUrl: string,
  userPrompt: string,
  messageHistory: ChatMessageV3[] = [],
  spacesNames: SpacesName[] = []
): Promise<ChatSuccessResponseV3> {
  const requestBody: ChatRequestBodyV3 = {
    userPrompt,
    messageHistory,
    spacesNames,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorData: BackendErrorResponseV3 = {
        error: `HTTP error! Status: ${response.status}`,
      };
      try {
        // Try to parse backend error response
        errorData = await response.json();
      } catch (e) {
        // Ignore if parsing fails, use the basic HTTP error message
        console.warn("Could not parse error response body as JSON.");
      }
      // Combine error messages if details are available
      const errorMessage = errorData.details
        ? `${errorData.error} - Details: ${errorData.details}`
        : errorData.error;
      throw new Error(errorMessage);
    }

    const data: ChatSuccessResponseV3 = await response.json();

    // Basic validation of the successful response structure
    if (!data || typeof data !== "object") {
      throw new Error("Invalid response structure received from the API.");
    }

    return data;
  } catch (error) {
    console.error("Error calling chat API V3:", error);
    // Re-throw the error to be handled by the caller
    // Ensure it's an Error object
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("An unknown error occurred during the API call.");
    }
  }
}
